import { createBrowserHistory, createMemoryHistory } from 'history';
import { useContext, createContext } from 'react';
import { addMiddleware, types, Instance, getSnapshot, applySnapshot, castToSnapshot } from 'mobx-state-tree';
import { connectReduxDevtools } from 'mst-middlewares';
import { syncHistoryWithStore } from 'mst-react-router';

import { CompanyDashboardStore } from '@src/features/dashboard';
import { JobsStore } from '@src/features/job';
import { FeedStore } from '@src/features/feed';
import { IndependentRecruiters } from '@src/features/independentRecruiters';
import createHistoryHashObserver from 'src/helpers/createHistoryHashObserver';

import { fetchingMiddleware, modifyRequestArgsMiddleware } from './middlewares';
import { RouterStore } from './RouterStore';
import { AuthStore } from './AuthStore';
import { UserStore } from './UserStore';
import { ResourcesStore } from './ResourcesStore';
import { CompanyStore } from './CompanyStore';
import { ApplicantsStore } from './ApplicantsStore';
import { CompanyPreferencesStore } from './CompanyPreferences';
import { TestStore } from './TestStore';

const RootModel = types.model('RootModel', {
  routerStore: RouterStore,
  authStore: AuthStore,
  userStore: UserStore,
  resourcesStore: ResourcesStore,
  companyStore: CompanyStore,
  jobsStore: JobsStore,
  companyPreferencesStore: CompanyPreferencesStore,
  companyDashboardStore: CompanyDashboardStore,
  applicantsStore: ApplicantsStore,
  feed: FeedStore,
  independentRecruiters: IndependentRecruiters,
  testStore: TestStore
});

export const rootStore = RootModel.create({
  routerStore: RouterStore.create(),
  authStore: AuthStore.create(),
  userStore: castToSnapshot(UserStore.create()),
  resourcesStore: castToSnapshot(ResourcesStore.create()),
  companyDashboardStore: CompanyDashboardStore.create({
    modal: { jobs: [] },
    data: [],
    filters: { activeFilters: {} }
  }),
  companyStore: CompanyStore.create(),
  companyPreferencesStore: CompanyPreferencesStore.create(),
  applicantsStore: castToSnapshot(ApplicantsStore.create()),
  feed: FeedStore.create({ jobs: {} }),
  independentRecruiters: IndependentRecruiters.create({}),
  testStore: TestStore.create(),
  jobsStore: castToSnapshot(JobsStore.create())
});

addMiddleware(rootStore, fetchingMiddleware);
addMiddleware(rootStore, modifyRequestArgsMiddleware);

export const history = typeof window !== 'undefined' ? createBrowserHistory() : createMemoryHistory();
createHistoryHashObserver(history);
syncHistoryWithStore(history, rootStore.routerStore);

export type IRootStore = Instance<typeof RootModel>;
const RootStoreContext = createContext<IRootStore>(rootStore as IRootStore);

export const MSTProvider = RootStoreContext.Provider;

export function useStore() {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider');
  }
  return store;
}

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && !(window as any).store) {
  (window as any).store = rootStore;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  connectReduxDevtools(require('remotedev'), rootStore);
}

if (module.hot) {
  if (module.hot.data && module.hot.data.rootStore) {
    applySnapshot(rootStore, module.hot.data.rootStore);
  }
  module.hot.dispose(data => {
    data.rootStore = getSnapshot(rootStore);
  });
}
